import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ShoppingCart, Package, Calendar, User, Building2, Send, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerPlaceOrder = () => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  
  const [formData, setFormData] = useState({
    xlarge_crates: 0,
    large_crates: 0,
    medium_crates: 0,
    small_crates: 0,
    cracks_crates: 0,
    expected_pickup_date: '',
    notes: ''
  });

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      // Fetch customer details to get affiliated distributor
      fetchCustomerData(parsedUser.name);
    }
  }, []);

  const fetchCustomerData = async (customerName) => {
    try {
      const response = await axios.get(`${API_URL}/api/customers/by-name/${encodeURIComponent(customerName)}`, getAuthHeader());
      setCustomerData(response.data);
    } catch (error) {
      console.log('Customer data not found, using user data');
    }
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const handleInputChange = (field, value) => {
    const numValue = Math.max(0, parseInt(value) || 0);
    setFormData({ ...formData, [field]: numValue });
  };

  const getTotalCrates = () => {
    return formData.xlarge_crates + formData.large_crates + formData.medium_crates + 
           formData.small_crates + formData.cracks_crates;
  };

  const getTotalEggs = () => {
    return getTotalCrates() * 30;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (getTotalCrates() === 0) {
      toast.error('Please enter at least one crate');
      return;
    }
    
    if (!formData.expected_pickup_date) {
      toast.error('Please select an expected pick-up date');
      return;
    }

    // Show confirmation modal instead of submitting directly
    setShowConfirmModal(true);
  };

  const confirmSubmit = async () => {
    setSubmitting(true);
    setShowConfirmModal(false);
    
    try {
      await axios.post(`${API_URL}/api/order-requests`, {
        xlarge_crates: formData.xlarge_crates,
        large_crates: formData.large_crates,
        medium_crates: formData.medium_crates,
        small_crates: formData.small_crates,
        cracks_crates: formData.cracks_crates,
        expected_pickup_date: formData.expected_pickup_date,
        delivery_date: formData.expected_pickup_date,
        notes: formData.notes,
        distributor_id: customerData?.affiliated_distributor_id || '',
        distributor_name: customerData?.affiliated_distributor_name || ''
      }, getAuthHeader());
      
      toast.success('Order request submitted successfully!');
      
      // Reset form
      setFormData({
        xlarge_crates: 0,
        large_crates: 0,
        medium_crates: 0,
        small_crates: 0,
        cracks_crates: 0,
        expected_pickup_date: '',
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit order');
    } finally {
      setSubmitting(false);
    }
  };

  const eggSizes = [
    { key: 'xlarge_crates', label: 'X-Large', color: 'bg-purple-100 border-purple-300' },
    { key: 'large_crates', label: 'Large', color: 'bg-blue-100 border-blue-300' },
    { key: 'medium_crates', label: 'Medium', color: 'bg-green-100 border-green-300' },
    { key: 'small_crates', label: 'Small', color: 'bg-yellow-100 border-yellow-300' },
    { key: 'cracks_crates', label: 'Cracks', color: 'bg-orange-100 border-orange-300' }
  ];

  return (
    <div className="space-y-6" data-testid="customer-place-order">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ShoppingCart className="w-7 h-7 text-emerald-600" />
            Place Order
          </h1>
          <p className="text-slate-500">Order eggs in crates (30 eggs per crate)</p>
        </div>
      </div>

      {/* Customer & Distributor Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <User className="w-8 h-8 text-emerald-600" />
              <div>
                <p className="text-sm text-emerald-600">Customer</p>
                <p className="text-lg font-semibold text-emerald-800">{user?.name || 'Loading...'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-sm text-blue-600">Affiliated Distributor</p>
                <p className="text-lg font-semibold text-blue-800">
                  {customerData?.affiliated_distributor_name || user?.affiliated_distributor_name || 'Direct to Farm (No Distributor)'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Order Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Enter Order Quantities
          </CardTitle>
          <CardDescription>
            Enter the number of crates for each egg size. Each crate contains 30 eggs.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Egg Size Inputs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {eggSizes.map((size) => (
                <div key={size.key} className={`p-4 rounded-lg border-2 ${size.color}`}>
                  <Label className="text-sm font-semibold text-slate-700">{size.label}</Label>
                  <div className="mt-2">
                    <Input
                      type="number"
                      min="0"
                      value={formData[size.key]}
                      onChange={(e) => handleInputChange(size.key, e.target.value)}
                      className="text-center text-lg font-bold"
                      data-testid={`input-${size.key}`}
                    />
                    <p className="text-xs text-slate-500 mt-1 text-center">
                      = {formData[size.key] * 30} eggs
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="bg-slate-100 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Total Crates:</span>
                <span className="text-xl font-bold text-slate-800">{getTotalCrates()}</span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-slate-600">Total Eggs:</span>
                <span className="text-xl font-bold text-emerald-600">{getTotalEggs()}</span>
              </div>
            </div>

            {/* Expected Pick-up Date */}
            <div>
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Expected Pick-up Date
              </Label>
              <Input
                type="date"
                value={formData.expected_pickup_date}
                onChange={(e) => setFormData({ ...formData, expected_pickup_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className="mt-1"
                required
                data-testid="input-pickup-date"
              />
            </div>

            {/* Notes */}
            <div>
              <Label>Additional Notes (Optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any special instructions or notes..."
                className="mt-1"
                rows={3}
                data-testid="input-notes"
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={submitting || getTotalCrates() === 0}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="submit-order-btn"
            >
              {submitting ? (
                'Submitting...'
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Order Request
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Confirmation Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="max-w-md" data-testid="confirm-order-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle className="w-6 h-6" />
              Confirm Order Submission
            </DialogTitle>
            <DialogDescription>
              Please review your order before submitting
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Order Summary */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <h4 className="font-semibold text-slate-700">Order Summary</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {formData.xlarge_crates > 0 && (
                  <div className="flex justify-between">
                    <span className="text-purple-600">X-Large:</span>
                    <span className="font-medium">{formData.xlarge_crates} crates ({formData.xlarge_crates * 30} eggs)</span>
                  </div>
                )}
                {formData.large_crates > 0 && (
                  <div className="flex justify-between">
                    <span className="text-blue-600">Large:</span>
                    <span className="font-medium">{formData.large_crates} crates ({formData.large_crates * 30} eggs)</span>
                  </div>
                )}
                {formData.medium_crates > 0 && (
                  <div className="flex justify-between">
                    <span className="text-green-600">Medium:</span>
                    <span className="font-medium">{formData.medium_crates} crates ({formData.medium_crates * 30} eggs)</span>
                  </div>
                )}
                {formData.small_crates > 0 && (
                  <div className="flex justify-between">
                    <span className="text-yellow-600">Small:</span>
                    <span className="font-medium">{formData.small_crates} crates ({formData.small_crates * 30} eggs)</span>
                  </div>
                )}
                {formData.cracks_crates > 0 && (
                  <div className="flex justify-between">
                    <span className="text-orange-600">Cracks:</span>
                    <span className="font-medium">{formData.cracks_crates} crates ({formData.cracks_crates * 30} eggs)</span>
                  </div>
                )}
              </div>
              
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between font-semibold">
                  <span>Total:</span>
                  <span className="text-emerald-600">{getTotalCrates()} crates ({getTotalEggs()} eggs)</span>
                </div>
              </div>
            </div>
            
            {/* Pickup Date */}
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-slate-500" />
              <span className="text-slate-600">Expected Pick-up:</span>
              <span className="font-medium">{new Date(formData.expected_pickup_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
            
            {/* Distributor */}
            <div className="flex items-center gap-2 text-sm">
              <Building2 className="w-4 h-4 text-slate-500" />
              <span className="text-slate-600">Distributor:</span>
              <span className="font-medium">{customerData?.affiliated_distributor_name || 'Direct to Farm'}</span>
            </div>
            
            {/* Notes */}
            {formData.notes && (
              <div className="text-sm">
                <span className="text-slate-600">Notes:</span>
                <p className="mt-1 p-2 bg-slate-100 rounded text-slate-700">{formData.notes}</p>
              </div>
            )}
            
            {/* Warning */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                Once submitted, this order will be sent to your affiliated distributor for processing. 
                You can track its status in the Order Requests tab.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowConfirmModal(false)}
              data-testid="cancel-confirm-btn"
            >
              Go Back & Edit
            </Button>
            <Button 
              onClick={confirmSubmit}
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={submitting}
              data-testid="confirm-submit-btn"
            >
              {submitting ? 'Submitting...' : 'Confirm & Submit Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerPlaceOrder;
